import React, { useEffect, useState } from 'react';
import { Info, ExternalLink, CheckCircle, AlertCircle } from 'lucide-react';
import { socialPlatforms } from './schema';
import { getPlayerProfile } from '../../../../services/firestoreService';
import { SizeSpacingPanel, ColorsFontsPanel } from '../common/BlockStyleSettings';
import { BLOCK_FEATURES, ALIGNMENT_OPTIONS, ICON_SIZE_OPTIONS } from '../common/commonDefaults';

/**
 * Returns extra style controls for the right-side style panel.
 */
export const getSocialLinksExtraStyleControls = ({ content, onChange }) => ({
  sizeSpacingExtras: (
    <div className="space-y-3">
      <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Icon Layout</h4>
      <div>
        <label className="block text-sm text-gray-300 mb-1">Alignment</label>
        <div className="flex gap-1.5">
          {ALIGNMENT_OPTIONS.map((opt) => (
            <button key={opt.value} type="button" onClick={() => onChange({ alignment: opt.value })}
              className={`flex-1 py-1.5 rounded text-sm font-medium transition-colors ${content.alignment === opt.value ? 'bg-[#749555] text-white' : 'bg-[#1a1a2e] text-gray-400 hover:text-white border border-gray-700'}`}>{opt.label}</button>
          ))}
        </div>
      </div>
      <div>
        <label className="block text-sm text-gray-300 mb-1">Icon Size</label>
        <div className="flex gap-1.5">
          {ICON_SIZE_OPTIONS.map((opt) => (
            <button key={opt.value} type="button" onClick={() => onChange({ size: opt.value })}
              className={`flex-1 py-1.5 rounded text-sm font-medium transition-colors ${content.size === opt.value ? 'bg-[#749555] text-white' : 'bg-[#1a1a2e] text-gray-400 hover:text-white border border-gray-700'}`}>{opt.label}</button>
          ))}
        </div>
      </div>
    </div>
  ),
});

/**
 * SocialLinksEditor - Editor component for social-links block
 * Now syncs with profile data instead of manual URL entry
 */
const SocialLinksEditor = ({ block, onChange, profileId, theme, hideStylePanels }) => {
  const { content } = block;
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  // Get platform value from profile
  const getPlatformValue = (platform) => {
    if (!profile) return null;
    const parts = platform.profileField.split('.');
    let value = profile;
    for (const part of parts) {
      value = value?.[part];
      if (!value) return null;
    }
    return value;
  };

  // Get platforms with values
  const platformsWithValues = socialPlatforms.filter((p) => getPlatformValue(p));

  // Check if platform is visible
  const isPlatformVisible = (platformValue) => {
    const visiblePlatforms = content.visiblePlatforms || [];
    // If empty array, all platforms with values are shown
    if (visiblePlatforms.length === 0) return true;
    return visiblePlatforms.includes(platformValue);
  };

  useEffect(() => {
    const loadProfile = async () => {
      if (!profileId) {
        setLoading(false);
        return;
      }

      try {
        const fetchedProfile = await getPlayerProfile(profileId);
        setProfile(fetchedProfile);
      } catch (error) {
        console.error('Error loading profile:', error);
      } finally {
        setLoading(false);
      }
    };

    loadProfile();
  }, [profileId]);

  const handleChange = (field, value) => {
    onChange({ [field]: value });
  };

  const handleStyleChange = (styleUpdates) => {
    onChange({ style: { ...(content.style || {}), ...styleUpdates } });
  };

  const togglePlatform = (platformValue) => {
    const currentVisible = content.visiblePlatforms || [];

    // If empty (showing all), switch to showing only selected ones
    if (currentVisible.length === 0) {
      // Start with all platforms with values, then remove the toggled one
      const allWithValues = platformsWithValues.map((p) => p.value);
      const newVisible = allWithValues.filter((v) => v !== platformValue);
      handleChange('visiblePlatforms', newVisible);
    } else if (currentVisible.includes(platformValue)) {
      // Remove platform
      handleChange('visiblePlatforms', currentVisible.filter((v) => v !== platformValue));
    } else {
      // Add platform
      handleChange('visiblePlatforms', [...currentVisible, platformValue]);
    }
  };

  const showAllPlatforms = () => {
    handleChange('visiblePlatforms', []);
  };

  const extras = getSocialLinksExtraStyleControls({ content, onChange });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8 text-gray-400">
        <div className="animate-spin w-5 h-5 border-2 border-gray-600 border-t-[#749555] rounded-full mr-2"></div>
        Loading...
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Info Card */}
      <div className="bg-blue-900/20 border border-blue-600/30 rounded-lg p-3">
        <div className="flex items-start gap-2">
          <Info size={16} className="text-blue-400 mt-0.5 flex-shrink-0" />
          <p className="text-xs text-blue-200">
            Links are automatically synced from your profile settings. Toggle which platforms to show below.
          </p>
        </div>
      </div>

      {/* Heading */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1">
          Heading (optional)
        </label>
        <input
          type="text"
          value={content.heading || ''}
          onChange={(e) => handleChange('heading', e.target.value)}
          placeholder="Follow Me"
          className="w-full px-3 py-2 bg-[#1a1a2e] border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-[#749555] focus:outline-none"
        />
      </div>

      {/* Icon Style */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1">
          Style
        </label>
        <div className="flex gap-2">
          {[
            { value: 'filled', label: 'Filled' },
            { value: 'outline', label: 'Outline' },
            { value: 'minimal', label: 'Minimal' },
          ].map((option) => (
            <button
              key={option.value}
              onClick={() => handleChange('iconStyle', option.value)}
              className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                (content.iconStyle || (typeof content.style === 'string' ? content.style : 'filled')) === option.value
                  ? 'bg-[#749555] text-white'
                  : 'bg-[#1a1a2e] text-gray-400 hover:text-white border border-gray-700'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      {/* Platform Visibility */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="block text-sm font-medium text-gray-300">
            Visible Platforms ({platformsWithValues.length} available)
          </label>
          {(content.visiblePlatforms || []).length > 0 && (
            <button
              onClick={showAllPlatforms}
              className="text-xs text-[#749555] hover:text-[#8bab6a] transition-colors"
            >
              Show All
            </button>
          )}
        </div>

        {platformsWithValues.length === 0 ? (
          <div className="bg-[#1a1a2e] border border-gray-700 rounded-lg p-4 text-center">
            <AlertCircle size={24} className="mx-auto mb-2 text-amber-400" />
            <p className="text-sm text-gray-400 mb-3">
              No social links configured in your profile
            </p>
            <a
              href="/dashboard/website/social-links"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-sm text-[#749555] hover:text-[#8bab6a]"
            >
              Add links in profile settings
              <ExternalLink size={12} />
            </a>
          </div>
        ) : (
          <div className="space-y-2">
            {platformsWithValues.map((platform) => {
              const isVisible = isPlatformVisible(platform.value);
              const value = getPlatformValue(platform);

              return (
                <div
                  key={platform.value}
                  className={`flex items-center justify-between bg-[#1a1a2e] border rounded-lg p-3 transition-colors cursor-pointer ${
                    isVisible ? 'border-[#749555]/50' : 'border-gray-700 opacity-60'
                  }`}
                  onClick={() => togglePlatform(platform.value)}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className={`w-5 h-5 rounded flex items-center justify-center ${
                        isVisible ? 'bg-[#749555]' : 'bg-gray-700'
                      }`}
                    >
                      {isVisible && <CheckCircle size={14} className="text-white" />}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-white">{platform.label}</p>
                      <p className="text-xs text-gray-500 truncate max-w-[180px]">{value}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Style Panels */}
      {!hideStylePanels && (
        <>
          <SizeSpacingPanel style={content.style || {}} onChange={handleStyleChange} features={BLOCK_FEATURES['social-links']} extraControls={extras.sizeSpacingExtras} />
          <ColorsFontsPanel style={content.style || {}} onChange={handleStyleChange} features={BLOCK_FEATURES['social-links']} theme={theme || {}} blockType="social-links" />
        </>
      )}

      {/* Edit Links Link */}
      <a
        href="/dashboard/website/social-links"
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center justify-center gap-2 w-full py-2.5 bg-[#749555] hover:bg-[#8bab6a] text-white rounded-lg transition-colors text-sm font-medium"
      >
        Edit Social Links
        <ExternalLink size={14} />
      </a>
    </div>
  );
};

export default SocialLinksEditor;
